import React, { useState } from "react";

import { isFloat, round } from "../../../tools";
import dayjs from "dayjs";
import { DATESYSTEM_TO_FORMAT } from "../../../pages/subpages/Settings";
import { useAppStore } from "../../../shared/stores/useAppStore";
import { useEditorStore } from "../../../shared/stores/useEditorStore";
import FieldDependenciesPanel from "./reusables/FieldDependenciesPanel";

export default function OutputTimeSerieField({ name, field, compareWithField, scrollRef, onScroll }) {
    const userSettings = useAppStore(s => s.userSettings);
    const scenarioId = useEditorStore(s => s.scenarioId);

    // Build a map of date -> value from compareWithField for quick lookup
    const compareMap = React.useMemo(() => {
        if (!compareWithField?.currentValue?.value?.data) return null;
        const map = new Map();
        compareWithField.currentValue.value.data.forEach((entry) => {
            const dateKey = dayjs(entry.date.value).format('YYYY-MM-DD');
            map.set(dateKey, entry.value.value);
        });
        return map;
    }, [compareWithField]);

    const isDifferent = (dateValue, currentValue) => {
        if (!compareMap) return false;
        const dateKey = dayjs(dateValue).format('YYYY-MM-DD');
        const compareValue = compareMap.get(dateKey);
        if (compareValue === undefined) return false;
        const tolerance = field.is_percentage ? 0.0001 : 0.01;
        return Math.abs(currentValue - compareValue) > tolerance;
    };

    const [showDeps, setShowDeps] = useState(false);

    return (
        <div className="ml-3">
            {field.currentValue.value.unit?.value && (
                <div className="mb-1">
                    Unit : {field.currentValue.value.unit.value}
                </div>
            )}
            {!compareWithField && (
                <FieldDependenciesPanel
                    isOpen={showDeps}
                    onClose={() => setShowDeps(false)}
                    scenarioId={scenarioId}
                    fieldId={field.id}
                    fieldName={name}
                />
            )}
            <div className="mt-2 mr-2">
                <div className="border border-slate-200 rounded-lg overflow-auto" ref={scrollRef} onScroll={onScroll}>
                    <table className="min-w-[650px] w-full border-collapse text-sm">
                        <thead className="bg-slate-50">
                            <tr className="border-b border-slate-200">
                                <th className="p-2 text-left" title="Beginning of Period"><b>Start</b></th>
                                {!compareWithField && (
                                    <th className="p-1 w-10 text-center" rowSpan={2}>
                                        <button
                                            onClick={() => setShowDeps(true)}
                                            className="p-1 rounded hover:bg-blue-100 text-blue-500 hover:text-blue-700 transition-colors"
                                            title="View dependencies"
                                        >
                                            <span className="material-symbols-outlined text-[18px]">info</span>
                                        </button>
                                    </th>
                                )}
                                {
                                    field.currentValue.value.data.map((e, index) => (
                                        <th key={index} className="p-2 text-left">{dayjs(e.date.value).format(DATESYSTEM_TO_FORMAT[userSettings.dateSystem])}</th>
                                    ))
                                }
                            </tr>
                            <tr className="border-b border-slate-200">
                                <th className="p-2 text-left" title="End of Period"><b>End</b></th>
                                {
                                    field.currentValue.value.data.map((e, index) => (
                                        <th key={index} className="p-2 text-left">{dayjs(e.end_of_period.value).format(DATESYSTEM_TO_FORMAT[userSettings.dateSystem])}</th>
                                    ))
                                }
                            </tr>
                        </thead>
                        <tbody>
                            <tr className="border-b border-slate-200 hover:bg-slate-50">
                                <td className="p-2"><b>Value</b></td>
                                {!compareWithField && <td></td>}
                                {
                                    field.currentValue.value.data.map((e, index) => {
                                        const cellValue = field.is_percentage ? e.value.value * 100 : e.value.value;
                                        const hasDifference = isDifferent(e.date.value, e.value.value);
                                        const cellClass = hasDifference
                                            ? "p-2 bg-yellow-100 font-bold"
                                            : "p-2";

                                        if (isFloat(cellValue) && field.roundable) {
                                            return (
                                                <td key={index} className={cellClass}>{round(cellValue, userSettings.roundPrecision).toLocaleString(userSettings.numberFormattingLocale, {maximumFractionDigits: userSettings.roundPrecision})}{field.is_percentage ? ' %' : ''}</td>
                                            )
                                        } else if (isFloat(cellValue)) {
                                            return (
                                                <td key={index} className={cellClass}>{cellValue.toLocaleString(userSettings.numberFormattingLocale)}{field.is_percentage ? ' %' : ''}</td>
                                            )
                                        } else {
                                            return (
                                                <td key={index} className={cellClass}>{cellValue}</td>
                                            )
                                        }
                                    })
                                }
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
            {/* OutputFieldFooter not yet implemented */}
        </div>
    )
}
